import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useStore } from "zustand";
import { GridList, GridListItem } from "react-aria-components";
import {
  Card,
  CardPreview,
  Collection,
  Content,
  Text,
  Image,
  SkeletonCollection,
  IllustratedMessage,
  Heading,
  Button,
  LinkButton,
  mergeStyles,
} from "@react-spectrum/s2";
import EmptyIcon from "@react-spectrum/s2/illustrations/linear/FolderOpen";
import AddCircleIcon from "@react-spectrum/s2/icons/AddCircle";
import PlayIcon from "@react-spectrum/s2/icons/Play";
import ChevronLeftIcon from "@react-spectrum/s2/icons/ChevronLeft";
import ChevronRightIcon from "@react-spectrum/s2/icons/ChevronRight";

import { getQueryLoadingState } from "@app/utils";
import { store } from "@app/store";
import { PreviewPasswordPopover } from "@app/components/PreviewPasswordPopover";

import {
  style,
  iconStyle,
  size,
  lightDark,
} from "@react-spectrum/s2/style" with { type: "macro" };

const cardContentStyles = style({
  pointerEvents: "none",
  display: "grid",
  size: "full",
  boxSizing: "border-box",
  padding: 16,
  gridTemplateAreas: [
    "header header",
    ". .",
    "badge .",
    "title icon",
    "description actions",
  ],
  gridTemplateRows: "1fr minmax(0, auto) repeat(2, minmax(0, max-content))",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, max-content)",
  columnGap: 12,
  rowGap: 4,
  position: "absolute",
  inset: 0,
});

const cardHeaderStyles = style({
  pointerEvents: "auto",
  gridArea: "header",
  marginStart: "auto",
});

const cardTitleStyles = style({
  gridArea: "title",
  font: "title-lg",
  color: "white",
  margin: 0,
});

const cardIconStyles = iconStyle({
  gridArea: "icon",
  alignSelf: "baseline",
  color: "white",
});

const cardDescriptionStyle = style({
  gridArea: "description",
  flexShrink: 1,
  font: "ui",
  color: lightDark("gray-100", "gray-800"),
});

const scrollArrowStyles = style({
  position: "absolute",
  zIndex: 1,
  top: "50%",
  translateY: `calc(-50% - ${size(12)})`,
  left: {
    default: "unset",
    isLeft: { default: -40, isLargeViewport: -48 },
  },
  right: {
    default: { default: -40, isLargeViewport: -48 },
    isLeft: "unset",
  },
  borderRadius: "full",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "gray-200",
  boxShadow: "elevated",
});

const renderEmptyState = (
  message = "No templates found",
  icon = <EmptyIcon />,
) => (
  <IllustratedMessage
    size="M"
    styles={style({
      aspectRatio: "3 / 4",
      marginTop: 48,
    })}
  >
    {icon}
    <Heading>{message}</Heading>
  </IllustratedMessage>
);

const TemplateBadge = ({ children, styles }) => {
  return (
    <div
      className={mergeStyles(
        style({
          gridArea: "badge",
          justifySelf: "start",
          paddingX: 8,
          paddingY: 2,
          borderWidth: 1,
          borderColor: "transparent-white-1000",
          borderRadius: "sm",
          backgroundColor: "transparent-white-800",
          marginBottom: 4,
          font: "ui-sm",
          color: "black",
        }),
        styles,
      )}
    >
      {children}
    </div>
  );
};

/**
 * What a template card shows over its thumbnail: its name, and on hover its
 * description and actions — `Preview`, with the template's `previewPassword`
 * in a popover. The hover content stays up while that popover is open, as
 * the pointer leaves the card for it.
 */
const TemplateCardOverlay = ({ item, isHovered, isNew }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isShown = isHovered || isPreviewOpen;

  const previewLink = (
    <LinkButton
      href={item.previewUrl}
      target="_blank"
      rel="noopener noreferrer"
      size="S"
      staticColor="white"
    >
      <PlayIcon />
      <Text>Preview</Text>
    </LinkButton>
  );

  return (
    <div
      className={style({
        backgroundImage:
          "linear-gradient(color(display-p3 0 0 0 / 0) 54.63%, color(display-p3 0 0 0 / 0.3) 100%), linear-gradient(color(display-p3 0 0 0 / 0) 61.59%, color(display-p3 0 0 0 / 0.9) 100%)",
        position: "absolute",
        inset: 0,
      })}
    >
      <div
        className={mergeStyles(
          cardContentStyles,
          style({
            opacity: {
              isShown: 0,
              default: 1,
            },
            translateY: {
              isShown: 12,
              default: 0,
            },
            transition: "all",
          })({ isShown }),
        )}
      >
        {isNew && <TemplateBadge>New</TemplateBadge>}
        <Text styles={cardTitleStyles}>{item.name}</Text>
        <AddCircleIcon styles={cardIconStyles} />
      </div>
      <div
        className={mergeStyles(
          cardContentStyles,
          style({
            opacity: {
              isShown: 1,
              default: 0,
            },
            translateY: {
              isShown: 0,
              default: 12,
            },
            transition: "all",
          })({ isShown }),
        )}
      >
        {!!item?.previewUrl && (
          <div className={cardHeaderStyles}>
            {item.previewPassword ? (
              <PreviewPasswordPopover
                password={item.previewPassword}
                onOpenChange={setIsPreviewOpen}
              >
                {previewLink}
              </PreviewPasswordPopover>
            ) : (
              previewLink
            )}
          </div>
        )}
        {isNew && <TemplateBadge>New</TemplateBadge>}
        <Text styles={cardTitleStyles}>{item.name}</Text>
        {item.description && (
          <Text styles={cardDescriptionStyle}>{item.description}</Text>
        )}
        <div
          className={style({
            flexShrink: 0,
            gridArea: "actions",
            alignSelf: "end",
          })}
        >
          <Button staticColor="white" fillStyle="outline">
            <Text>Try it</Text>
          </Button>
        </div>
      </div>
    </div>
  );
};

TemplateCardOverlay.propTypes = {
  item: PropTypes.object.isRequired,
  isHovered: PropTypes.bool,
  isNew: PropTypes.bool,
};

/**
 * Stateless templates list view. Renders the selectable grid of template
 * cards. All state is provided by the caller through props so this component
 * can be reused anywhere a template picker is needed.
 */
export const TemplatesList = ({
  templates,
  selectedTemplateKey,
  onSelectionChange,
  isDisabled = false,
  emptyStateMessage = "Session expired - please sign in again",
  emptyStateIcon = <EmptyIcon />,
  placeholder,
  orientation = "vertical",
  styles = "",
  showScrollArrows = false,
}) => {
  const isLargeViewport = useStore(store, (state) => state.isLargeViewport);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const cardWidth = isLargeViewport ? size(300) : size(240);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!showScrollArrows || !el) return undefined;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    // Observe the container and its content, as content width changes when templates load
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [showScrollArrows, updateScrollState, templates.data?.length]);

  const scrollByDirection = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const renderScrollArrow = (direction) => {
    const isLeft = direction < 0;
    return (
      <div className={scrollArrowStyles({ isLeft, isLargeViewport })}>
        <Button
          aria-label={isLeft ? "Scroll left" : "Scroll right"}
          variant="secondary"
          isDisabled={isLeft ? !canScrollLeft : !canScrollRight}
          onPress={() => scrollByDirection(direction)}
        >
          {isLeft ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </Button>
      </div>
    );
  };

  const isNew = (item) =>
    +new Date() - new Date(item?.createdAt) < 1000 * 60 * 60 * 24 * 14;

  const content = (
    <div
      ref={scrollRef}
      className={style({
        boxSizing: "border-box",
        width: {
          default: {
            orientation: {
              horizontal: "auto",
            },
          },
          md: { orientation: { horizontal: "auto", vertical: "full" } },
        },
        overflowX: "auto",
        paddingBottom: { orientation: { horizontal: 24 } },
        marginX: {
          default: {
            orientation: {
              horizontal: -48,
              vertical: "auto",
            },
          },
          // md: "unset",
        },
        paddingX: {
          default: {
            orientation: {
              horizontal: 48,
            },
          },
          // md: "unset",
        },
      })({ orientation })}
    >
      <GridList
        loadingState={getQueryLoadingState(templates)}
        selectionMode="single"
        selectionStyle="checkbox"
        disallowEmptySelection
        selectedKeys={[selectedTemplateKey]}
        onSelectionChange={onSelectionChange}
        disabledKeys={
          isDisabled
            ? new Set(templates.data?.map((item) => item.id) ?? [])
            : new Set()
        }
        renderEmptyState={() =>
          !templates.isFetching &&
          renderEmptyState(emptyStateMessage, emptyStateIcon)
        }
        aria-label="Select a template"
        layout="grid"
        className={mergeStyles(
          style({
            display: "grid",
            // gridTemplateColumns: `repeat(3, minmax(0, ${size(300)}))`,
            // gridAutoRows: "minmax(0, max-content)",
            justifyContent: "center",
            alignItems: "center",
            gap: 12,
            width: {
              orientation: {
                horizontal: "fit",
                // vertical: "fit",
              },
            },
            gridAutoFlow: {
              orientation: {
                horizontal: "column",
              },
            },
          })({ orientation }),
          styles,
        )}
        style={{
          gridTemplateColumns:
            orientation === "horizontal"
              ? `repeat(3, ${cardWidth})`
              : `repeat(auto-fit, ${cardWidth})`,
          gridAutoColumns: cardWidth,
        }}
      >
        <Collection
          items={templates.data ?? []}
          dependencies={
            process.env.NODE_ENV === "development" ? [+new Date()] : []
          }
        >
          {(item) => (
            <GridListItem
              key={item.id}
              className={style({
                position: "relative",
                size: "full",
                borderRadius: "lg",
                cursor: "pointer",
              })}
              // style={{ minWidth: cardWidth }}
            >
              {({ isHovered }) => (
                <Card
                  id={item.id}
                  textValue={item.name}
                  styles={style({ size: "full" })}
                  // UNSAFE_style={{ width: cardWidth }}
                >
                  <CardPreview>
                    <Image
                      src={item.thumbnailUrl}
                      alt={item.name}
                      styles={style({
                        size: "full",
                        objectFit: "cover",
                        aspectRatio: "3 / 4",
                        scale: {
                          isHovered: 1.1,
                          default: 1,
                        },
                        translateY: {
                          isHovered: -16,
                          default: 0,
                        },
                        transition: "all",
                        transitionDuration: { isHovered: 5000, default: 0 },
                      })({ isHovered })}
                    />

                    <TemplateCardOverlay
                      item={item}
                      isHovered={isHovered}
                      isNew={isNew(item)}
                    />
                  </CardPreview>
                </Card>
              )}
            </GridListItem>
          )}
        </Collection>
        {templates.isFetching && !templates.data?.length ? (
          <SkeletonCollection>
            {() => (
              <Card styles={style({ size: "full" })}>
                <CardPreview>
                  <Image
                    alt=""
                    styles={style({
                      size: "full",
                      objectFit: "cover",
                      aspectRatio: "3 / 4",
                    })}
                  />
                </CardPreview>
              </Card>
            )}
          </SkeletonCollection>
        ) : placeholder ? (
          <GridListItem
            isDisabled
            className={style({
              position: "relative",
              size: "full",
              borderRadius: "lg",
            })}
          >
            {placeholder}
          </GridListItem>
        ) : null}
      </GridList>
    </div>
  );

  if (!showScrollArrows) return content;

  return (
    <div className={style({ position: "relative" })}>
      {content}
      {renderScrollArrow(-1)}
      {renderScrollArrow(1)}
    </div>
  );
};

TemplatesList.propTypes = {
  templates: PropTypes.object.isRequired,
  selectedTemplateKey: PropTypes.string,
  onSelectionChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  emptyStateMessage: PropTypes.string,
  emptyStateIcon: PropTypes.node,
  placeholder: PropTypes.node,
  orientation: PropTypes.oneOf(["vertical", "horizontal"]),
  styles: PropTypes.string,
  showScrollArrows: PropTypes.bool,
};
